<template>
    <TransitionHeight ref="transitionHeightRef">
        <nav
            v-if="tabs.length"
            class="mb-[2px] flex justify-between"
            :class="{
                'border-b border-grind-200': isExpanded,
                [tabsContainerClass]: !scrollingRequired,
            }"
        >
            <div
                v-show="scrollingRequired"
                class="relative -mb-[2px] flex items-center px-2 xs:px-4"
            >
                <a href="#" @click.prevent="scrollPrev">
                    <Icon
                        name="chevron-left"
                        class="text-[12px] transition"
                        :class="scrollLeftEnabled ? '' : 'text-grind-200'"
                    />
                </a>

                <span
                    :class="scrollLeftEnabled ? '' : 'opacity-0'"
                    class="pointer-events-none absolute -right-8 bottom-[3px] top-0 z-10 w-8 bg-gradient-to-r from-white to-transparent transition-opacity"
                ></span>
            </div>

            <ul
                ref="scrollContainer"
                class="scrollbar-none relative -mb-[2px] flex gap-1 overflow-x-scroll scroll-smooth"
            >
                <li
                    v-for="tab in tabs"
                    v-show="tab.enabled"
                    :key="tab.key"
                    class="text-center"
                >
                    <a
                        href="#"
                        class="group relative block whitespace-nowrap p-2 transition-colors xs:px-4 xs:py-3 xs:text-lg md:px-6 md:py-4 md:text-xl lg:py-5"
                        :class="
                            tab.key === currentTabPreTransition
                                ? ''
                                : 'hover:text-brew'
                        "
                        @click.prevent="setCurrentTab(tab.key)"
                    >
                        <span
                            class="font-bold"
                            :class="
                                isExpanded &&
                                tab.key === currentTabPreTransition
                                    ? ''
                                    : 'opacity-0'
                            "
                        >
                            {{ tab.title }}
                        </span>

                        <span
                            class="absolute inset-0 flex items-center justify-center"
                            :class="
                                isExpanded &&
                                tab.key === currentTabPreTransition
                                    ? 'opacity-0'
                                    : ''
                            "
                        >
                            {{ tab.title }}
                        </span>

                        <span
                            class="absolute bottom-0 left-0 right-0 h-[3px] rounded-full transition-colors"
                            :class="
                                isExpanded &&
                                tab.key === currentTabPreTransition
                                    ? 'bg-grind'
                                    : 'bg-transparent'
                            "
                        ></span>
                    </a>
                </li>
            </ul>

            <div
                v-show="scrollingRequired"
                class="relative -mb-[2px] flex items-center px-2 xs:px-4"
            >
                <a href="#" @click.prevent="scrollNext">
                    <Icon
                        name="chevron-right"
                        class="text-[12px] transition"
                        :class="scrollRightEnabled ? '' : 'text-grind-200'"
                    />
                </a>

                <span
                    :class="scrollRightEnabled ? '' : 'opacity-0'"
                    class="pointer-events-none absolute -left-8 bottom-[3px] top-0 z-10 w-8 bg-gradient-to-l from-white to-transparent transition-opacity"
                ></span>
            </div>

            <div
                v-if="collapseEnabled"
                class="-mb-[2px] flex items-center pl-2"
                :class="{ 'pr-2': scrollingRequired }"
            >
                <button
                    data-cy="collapse-toggle"
                    class="-mr-2 p-2"
                    @click="toggleExpanded"
                >
                    <div
                        class="relative flex h-[30px] w-[30px] items-center justify-center rounded ring-1 ring-inset ring-grind transition"
                        :class="{
                            'bg-grind text-white': isExpanded,
                            'hover:bg-brew hover:text-white hover:ring-brew':
                                !titleIsClickable,
                            'group-hover:bg-brew group-hover:text-white group-hover:ring-brew':
                                titleIsClickable,
                        }"
                    >
                        <Icon
                            name="plus"
                            class="text-xl transition"
                            :class="{ 'opacity-0': isExpanded }"
                        />

                        <span
                            class="absolute inset-0 flex items-center justify-center"
                        >
                            <Icon
                                name="minus"
                                class="text-[22px] opacity-0 transition"
                                :class="{ 'opacity-100': isExpanded }"
                            />
                        </span>
                    </div>
                </button>
            </div>
        </nav>

        <div
            ref="contentContainer"
            class="duration-400 relative transition-all"
            :class="{
                'sm:translate-x-4': transitioning,
                'opacity-0': transitioning || !isExpanded,
                'h-0': !isExpanded,
            }"
        >
            <div :class="slotClass">
                <slot />
            </div>
        </div>
    </TransitionHeight>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es';
import { TabsItem } from '~/types/miscellaneous';
import TransitionHeight from '~/components/TransitionHeight.vue';

withDefaults(
    defineProps<{
        collapseEnabled?: boolean;
        tabsContainerClass?: string;
        slotClass?: string;
    }>(),
    {
        collapseEnabled: false,
        tabsContainerClass: '',
        slotClass: '',
    },
);

const contentContainer = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const transitionHeightRef = ref<InstanceType<typeof TransitionHeight> | null>(
    null,
);

const emit = defineEmits(['current-tab']);

const state = reactive<{
    tabs: TabsItem[];
    currentTabPreTransition: string | null;
    currentTabPostTransition: string | null;
    transitioning: boolean;
    isExpanded: boolean;
    scrollingRequired: boolean;
    currentScrollPosition: number;
    targetScrollPosition: number;
}>({
    tabs: [],
    currentTabPreTransition: null,
    currentTabPostTransition: null,
    transitioning: false,
    isExpanded: true,
    scrollingRequired: false,
    currentScrollPosition: 0,
    targetScrollPosition: 0,
});

const scrollLeftEnabled = computed<boolean>(() => {
    if (!state.scrollingRequired) {
        return false;
    }

    return state.currentScrollPosition > 0;
});

const scrollRightEnabled = computed<boolean>(() => {
    if (!state.scrollingRequired || !scrollContainer.value) {
        return false;
    }

    return (
        scrollContainer.value.clientWidth + state.currentScrollPosition <
        scrollContainer.value.scrollWidth
    );
});

const recalculateContentHeight = () => {
    if (transitionHeightRef.value) {
        transitionHeightRef.value.recalculateHeight();
    }
};

const maybeCenterTab = (tabKey: string) => {
    if (!scrollContainer.value) {
        return;
    }

    let newOffset = 0;
    const containerWidth = scrollContainer.value.clientWidth;
    const tabsWidth = scrollContainer.value.scrollWidth;

    if (tabsWidth > containerWidth) {
        const tabIndex = state.tabs.findIndex((tab) => tab.key === tabKey);
        if (tabIndex === -1) {
            return;
        }
        const tabEl = scrollContainer.value.children[tabIndex] as HTMLElement;
        if (!tabEl) {
            return;
        }

        const scrollContainerCenter = containerWidth / 2;
        const currentTabCenter = tabEl.offsetLeft + tabEl.clientWidth / 2;

        if (currentTabCenter > scrollContainerCenter) {
            newOffset = currentTabCenter - scrollContainerCenter;

            const maxOffset = tabsWidth - containerWidth;
            if (newOffset > maxOffset) {
                newOffset = maxOffset;
            }
        }
    }

    state.targetScrollPosition = newOffset;
};

const setCurrentTab = (tabKey: string) => {
    if (state.currentTabPreTransition === tabKey) {
        state.isExpanded = true;
        return;
    }

    maybeCenterTab(tabKey);

    if (!state.isExpanded || !state.currentTabPostTransition) {
        state.isExpanded = true;
        state.currentTabPreTransition = tabKey;
        state.currentTabPostTransition = tabKey;
        recalculateContentHeight();
    } else {
        state.transitioning = true;
        state.currentTabPreTransition = tabKey;

        setTimeout(() => {
            state.transitioning = false;
            state.currentTabPostTransition = tabKey;
            recalculateContentHeight();
        }, 400);
    }
};

const scrollPrev = () => {
    const currentTabIndex = state.tabs.findIndex(
        (tab) => tab.key === state.currentTabPreTransition,
    );
    if (currentTabIndex === -1 || !state.tabs[currentTabIndex - 1]) {
        return;
    }

    setCurrentTab(state.tabs[currentTabIndex - 1].key);
};

const scrollNext = () => {
    const currentTabIndex = state.tabs.findIndex(
        (tab) => tab.key === state.currentTabPreTransition,
    );
    if (currentTabIndex === -1 || !state.tabs[currentTabIndex + 1]) {
        return;
    }

    setCurrentTab(state.tabs[currentTabIndex + 1].key);
};

const toggleExpanded = () => {
    if (state.isExpanded) {
        setCurrentTab(state.tabs[0].key);
    }

    state.isExpanded = !state.isExpanded;
};

const {
    tabs,
    currentTabPreTransition,
    currentTabPostTransition,
    transitioning,
    isExpanded,
    scrollingRequired,
    targetScrollPosition,
} = toRefs(state);

watch(isExpanded, recalculateContentHeight);

watch(targetScrollPosition, (scrollPosition) => {
    if (scrollContainer.value) {
        scrollContainer.value.scrollLeft = scrollPosition;
    }
});

provide('currentTab', currentTabPostTransition);

provide('registerTab', (key: string, title: string, enabled: boolean) => {
    state.tabs.push({ key, title, enabled });

    if (!state.currentTabPreTransition) {
        state.currentTabPreTransition = key;
        state.currentTabPostTransition = key;
    }
});

provide('setTabEnabled', (key: string, enabled: boolean) => {
    const tabIndex = state.tabs.findIndex((tab) => tab.key === key);
    if (tabIndex !== -1) {
        state.tabs[tabIndex].enabled = enabled;

        if (state.currentTabPreTransition === key) {
            const firstEnabledTab = state.tabs.find((tab) => tab.enabled);
            if (firstEnabledTab) {
                setCurrentTab(firstEnabledTab.key);
            }
        }
    }
});

const updateScrollingRequired = () => {
    if (scrollContainer.value) {
        scrollingRequired.value =
            scrollContainer.value.scrollWidth >
            scrollContainer.value.clientWidth;
    }
};

onMounted(() => {
    window.addEventListener(
        'resize',
        debounce(() => {
            updateScrollingRequired();

            if (currentTabPostTransition.value) {
                maybeCenterTab(currentTabPostTransition.value);
            }
        }),
    );

    nextTick(() => {
        updateScrollingRequired();

        if (scrollContainer.value) {
            scrollContainer.value.addEventListener('scroll', (event) => {
                const { scrollLeft } = event.target as HTMLElement;
                state.currentScrollPosition = scrollLeft;
            });
        }
    });
});

watchEffect(() => {
    emit('current-tab', state.currentTabPostTransition);
});

defineExpose({ recalculateContentHeight });
</script>
